import React from "react";
import { generatedAssumptionImpactLedger } from "../data/generatedAssumptionImpactLedger.js";
import { generatedCapabilityMaturityLedger } from "../data/generatedCapabilityMaturityLedger.js";
import { generatedClaimEvidenceLedger } from "../data/generatedClaimEvidenceLedger.js";
import { generatedCustomerDecisionDossierLedger } from "../data/generatedCustomerDecisionDossierLedger.js";
import { generatedCustomerReportPacketLedger } from "../data/generatedCustomerReportPacketLedger.js";
import { generatedEconomicsEvidenceLedger } from "../data/generatedEconomicsEvidenceLedger.js";
import { generatedFeasibilityReport } from "../data/generatedFeasibilityReport.js";
import { generatedMaterialsProcessEvidenceLedger } from "../data/generatedMaterialsProcessEvidenceLedger.js";
import { generatedModelRunLedger } from "../data/generatedModelRunLedger.js";
import { generatedModelValidationProtocolLedger } from "../data/generatedModelValidationProtocols.js";
import { generatedPromotionClosureLedger } from "../data/generatedPromotionClosureLedger.js";
import { generatedReleaseEvidenceLedger } from "../data/generatedReleaseEvidenceLedger.js";
import { generatedResearchPackageManifest } from "../data/generatedResearchPackageManifest.js";
import { generatedRuntimeDependencyCustodyLedger } from "../data/generatedRuntimeDependencyCustodyLedger.js";
import { generatedRuntimeClosurePacket } from "../data/generatedRuntimeClosurePacket.js";
import { generatedRuntimeValidationCampaignLedger } from "../data/generatedRuntimeValidationCampaignLedger.js";
import { generatedScenarioCrossoverFrontierLedger } from "../data/generatedScenarioCrossoverFrontier.js";
import { generatedScenarioDecisionAuditLedger } from "../data/generatedScenarioDecisionAuditLedger.js";
import { generatedScenarioDecisionDeltaLedger } from "../data/generatedScenarioDecisionDeltaLedger.js";
import { generatedScenarioDecisionLedger } from "../data/generatedScenarioDecisionLedger.js";
import { generatedScenarioDiligenceWorkOrderLedger } from "../data/generatedScenarioDiligenceWorkOrderLedger.js";
import { generatedSidecarPromotionLedger } from "../data/generatedSidecarPromotionLedger.js";
import { generatedSidecarReadinessLedger } from "../data/generatedSidecarReadinessLedger.js";
import { generatedSourceIngestionLedger } from "../data/generatedSourceIngestionLedger.js";
import { generatedSourceUseClearanceDossier } from "../data/generatedSourceUseClearanceDossier.js";
import { generatedSpaceEnvironmentEvidenceLedger } from "../data/generatedSpaceEnvironmentEvidenceLedger.js";
import { generatedTrajectoryValidationMatrix } from "../data/generatedTrajectoryValidationMatrix.js";
import { generatedWorkspaceLedger } from "../data/generatedWorkspaceLedger.js";
import { CellStack, DataTable, EvidencePanel, Metric, PageHeader, StackList, StatusPill } from "./ui.jsx";

export function ResearchPackageEvidencePanels() {
  const packages = generatedResearchPackageManifest.packages;
  const artifactCount = packages.reduce((sum, item) => sum + item.includedArtifacts.length, 0);
  const topCapability = generatedCapabilityMaturityLedger.topCapabilities[0];
  const crossToolResidualCampaign = generatedRuntimeValidationCampaignLedger.campaigns.find(
    (campaign) => campaign.id === "campaign-cross-tool-residual-frontier",
  );
  const orekitClosureRecord = generatedRuntimeClosurePacket.closureRecords.find(
    (record) => record.contractId === "orekit-propagation-v1",
  );
  const gmatClosureRecord = generatedRuntimeClosurePacket.closureRecords.find(
    (record) => record.contractId === "gmat-transfer-v1",
  );
  const tudatClosureRecord = generatedRuntimeClosurePacket.closureRecords.find(
    (record) => record.contractId === "tudat-transfer-v1",
  );
  const trajectoryRows = generatedTrajectoryValidationMatrix.rows;
  const trajectoryCrossToolFrontier = generatedTrajectoryValidationMatrix.crossToolResidualFrontier;
  const spiceRuntimeArtifact = generatedSidecarPromotionLedger.validations.find((validation) =>
    validation.artifactId?.includes("spice-gf-runtime-mercury-clear-link-22deg-2025"),
  );
  const readinessContractWithPreflight = generatedSidecarReadinessLedger.contracts.find(
    (contract) => contract.bestActionablePreflight,
  );
  const runtimeDependencyRows = [...generatedRuntimeDependencyCustodyLedger.dependencyRecords].sort(
    (a, b) => a.acquisitionPriority - b.acquisitionPriority || a.tool.localeCompare(b.tool),
  );
  const packageRows = packages.map((item) => ({
    ...item,
    blockerCount: item.blockers.length,
    artifactCount: item.includedArtifacts.length,
    customerContractState: item.customerReadinessContract?.state ?? "not-generated",
    blockedProofGateCount: item.customerReadinessContract?.blockedProofGateIds?.length ?? 0,
  }));

  return (
    <section className="module">
      <PageHeader
        title="RESEARCH PACKAGES"
        muted="Customer-facing deliverables"
        subtitle="Beta package manifests bind workspaces, run hashes, included artifacts, review blockers, and allowed-use language before any external delivery."
      />
      <div className="metric-grid-4">
        <Metric label="Packages" value={generatedResearchPackageManifest.packageCount} />
        <Metric label="Blocked packages" value={generatedResearchPackageManifest.blockedPackageCount} />
        <Metric label="Customer contracts" value={generatedResearchPackageManifest.customerContractCount ?? packages.length} />
        <Metric label="Contract blockers" value={generatedResearchPackageManifest.blockedCustomerContractCount ?? packages.length} />
        <Metric label="Decision dossiers" value={generatedCustomerDecisionDossierLedger.scenarioCount} />
        <Metric label="Report packets" value={generatedCustomerReportPacketLedger.reportPacketCount} />
        <Metric label="Blocked reports" value={generatedCustomerReportPacketLedger.blockedReportPacketCount} />
        <Metric label="Paid beta dossiers" value={generatedCustomerDecisionDossierLedger.paidBetaDossierCount} />
        <Metric label="Diligence orders" value={generatedScenarioDiligenceWorkOrderLedger.workOrderCount} />
        <Metric label="P0 orders" value={generatedScenarioDiligenceWorkOrderLedger.p0WorkOrderCount} />
        <Metric label="Delta records" value={generatedScenarioDecisionDeltaLedger.deltaRecordCount} />
        <Metric label="Accepted deltas" value={generatedScenarioDecisionDeltaLedger.acceptedDeltaRecordCount} />
        <Metric label="Citable claims" value={generatedClaimEvidenceLedger.verifiedClaimCount} />
        <Metric label="Included artifacts" value={artifactCount} />
        <Metric label="Capabilities" value={generatedCapabilityMaturityLedger.capabilityCount} />
        <Metric label="Blocked capabilities" value={generatedCapabilityMaturityLedger.blockedCapabilityCount} />
        <Metric label="Validation protocols" value={generatedModelValidationProtocolLedger.protocolCount} />
        <Metric label="Protocol blockers" value={generatedModelValidationProtocolLedger.blockedProtocolCount} />
        <Metric label="Top capability" value={topCapability?.maturityBand ?? "n/a"} />
        <Metric label="Economics sources" value={generatedEconomicsEvidenceLedger.recordCount} />
        <Metric label="Econ blockers" value={generatedEconomicsEvidenceLedger.blockedRecordCount} />
        <Metric label="Process records" value={generatedMaterialsProcessEvidenceLedger.processCount} />
        <Metric label="Process blockers" value={generatedMaterialsProcessEvidenceLedger.blockedProcessCount} />
        <Metric label="Environment records" value={generatedSpaceEnvironmentEvidenceLedger.recordCount} />
        <Metric label="Environment blockers" value={generatedSpaceEnvironmentEvidenceLedger.blockedRecordCount} />
        <Metric label="Ingestion queue" value={generatedSourceIngestionLedger.workOrderCount} />
        <Metric label="Source blockers" value={generatedSourceIngestionLedger.blockedWorkOrderCount} />
        <Metric label="Clearance classes" value={generatedSourceUseClearanceDossier.classCount} />
        <Metric label="Clearance blockers" value={generatedSourceUseClearanceDossier.blockedSourceCount} />
        <Metric label="Release gates" value={generatedReleaseEvidenceLedger.gateCount} />
        <Metric label="Release blockers" value={generatedReleaseEvidenceLedger.blockedGateCount} />
        <Metric label="Closure plans" value={generatedPromotionClosureLedger.planCount} />
        <Metric label="Closure blockers" value={generatedPromotionClosureLedger.blockedClosurePlanCount} />
        <Metric label="Closure partial" value={generatedPromotionClosureLedger.partialClosurePlanCount} />
        <Metric label="Runtime campaigns" value={generatedRuntimeValidationCampaignLedger.campaignCount} />
        <Metric label="Campaign blockers" value={generatedRuntimeValidationCampaignLedger.blockedCampaignCount} />
        <Metric label="Runtime closure records" value={generatedRuntimeClosurePacket.contractCount} />
        <Metric label="Closure blocked" value={generatedRuntimeClosurePacket.blockedBeforeGenerationCount} />
        <Metric label="Dependency custody" value={generatedRuntimeDependencyCustodyLedger.contractCount} />
        <Metric label="Missing deps" value={generatedRuntimeDependencyCustodyLedger.missingDependencyCount} />
        <Metric label="Trajectory rows" value={generatedTrajectoryValidationMatrix.rowCount} />
        <Metric label="Trajectory blocked" value={generatedTrajectoryValidationMatrix.blockedRowCount} />
        <Metric label="Cross-tool state" value={trajectoryCrossToolFrontier.currentState} />
        <Metric label="Trajectory hash" value={generatedTrajectoryValidationMatrix.matrixHash.slice(0, 18)} />
        <Metric label="Ready to run" value={generatedSidecarReadinessLedger.readyToRunContractCount} />
        <Metric label="Readiness hash" value={generatedSidecarReadinessLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Assumption gates" value={generatedAssumptionImpactLedger.blockedAssumptionCount} />
        <Metric label="Decision audits" value={generatedScenarioDecisionAuditLedger.auditCount} />
        <Metric label="Crossovers" value={generatedScenarioCrossoverFrontierLedger.challengerCount} />
        <Metric label="Decision lane" value={generatedScenarioDecisionLedger.topDecision.decisionLane.replace("lane-", "L")} />
        <Metric label="Manifest hash" value={generatedResearchPackageManifest.manifestHash.slice(0, 18)} />
      </div>
      <EvidencePanel title="Sidecar execution packet" status={generatedRuntimeValidationCampaignLedger.mode}>
        <p>{generatedRuntimeValidationCampaignLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Runtime validation campaign" value={generatedRuntimeValidationCampaignLedger.campaignCount} />
          <Metric label="Runtime closure packet" value={generatedRuntimeClosurePacket.contractCount} />
          <Metric label="Dependency custody" value={generatedRuntimeDependencyCustodyLedger.blockedDependencyCustodyCount} />
          <Metric label="Missing deps" value={generatedRuntimeDependencyCustodyLedger.missingDependencyCount} />
          <Metric label="Campaign blockers" value={generatedRuntimeValidationCampaignLedger.blockedCampaignCount} />
          <Metric label="Closure packet hash" value={generatedRuntimeClosurePacket.packetHash.slice(0, 18)} />
          <Metric label="Dependency hash" value={generatedRuntimeDependencyCustodyLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Best preflight" value={readinessContractWithPreflight?.bestActionablePreflight?.preflightState ?? "no preflight"} />
          <Metric label="Ready to run" value={generatedSidecarReadinessLedger.readyToRunContractCount} />
          <Metric label="Readiness hash" value={generatedSidecarReadinessLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Campaign hash" value={generatedRuntimeValidationCampaignLedger.ledgerHash.slice(0, 18)} />
        </div>
        <StackList
          items={[
            {
              key: "cross-tool-residual-frontier",
              title: crossToolResidualCampaign?.title ?? "Cross-tool residual frontier",
              meta: `${crossToolResidualCampaign?.campaignStatus ?? "blocked"} / ${
                crossToolResidualCampaign?.priorityLane ?? "P0"
              }`,
              body:
                crossToolResidualCampaign?.nextAction ??
                "Run independent SPICE, Orekit, GMAT, and Tudat artifacts before promotion.",
            },
            {
              key: "spice-runtime-artifact",
              title: spiceRuntimeArtifact?.artifactId ?? "spice-gf-runtime-mercury-clear-link-22deg-2025",
              meta: "Runtime artifact / promotion evidence",
              body:
                spiceRuntimeArtifact?.promotionReason ??
                "Keep promoted runtime artifacts tied to current reproducibility and sidecar readiness checks.",
            },
            {
              key: "runtime-closure-packet",
              title: "Runtime closure packet",
              meta: `${generatedRuntimeClosurePacket.mode} / ${generatedRuntimeClosurePacket.blockedBeforeGenerationCount} blocked before generation`,
              body: generatedRuntimeClosurePacket.releaseImpact,
            },
          ]}
          getTitle={(item) => item.title}
          getMeta={(item) => item.meta}
          getBody={(item) => item.body}
        />
        <DataTable
          shell="shell"
          rows={[orekitClosureRecord, gmatClosureRecord, tudatClosureRecord].filter(Boolean)}
          getRowKey={(record) => record.contractId}
          columns={[
            {
              key: "tool",
              header: "Closure record",
              render: (record) => <CellStack title={record.tool} meta={record.contractId} />,
            },
            { key: "closureState", header: "State" },
            {
              key: "blockerClasses",
              header: "Blocker classes",
              render: (record) => record.blockerClasses.join(", ") || "none",
            },
            { key: "requiredOutputPath", header: "Required output" },
            {
              key: "validate",
              header: "Validate",
              render: (record) => record.executionCommands.validateArtifact,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Trajectory validation matrix" status={generatedTrajectoryValidationMatrix.mode} className="panel-large">
        <p>{generatedTrajectoryValidationMatrix.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Rows" value={generatedTrajectoryValidationMatrix.rowCount} />
          <Metric label="Blocked rows" value={generatedTrajectoryValidationMatrix.blockedRowCount} />
          <Metric label="Decision use" value={generatedTrajectoryValidationMatrix.decisionUseAllowed ? "allowed" : "blocked"} />
          <Metric label="Matrix hash" value={generatedTrajectoryValidationMatrix.matrixHash.slice(0, 18)} />
          <Metric label="cross-tool trajectory residual frontier" value={trajectoryCrossToolFrontier.id} />
          <Metric label="Residual state" value={trajectoryCrossToolFrontier.currentState} />
          <Metric label="Customer use" value={trajectoryCrossToolFrontier.customerUseAllowed ? "allowed" : "blocked"} />
          <Metric label="Frontier hash" value={trajectoryCrossToolFrontier.frontierHash.slice(0, 18)} />
        </div>
        <DataTable
          rows={trajectoryRows}
          getRowKey={(row) => row.contractId}
          columns={[
            {
              key: "contractId",
              header: "Contract",
              render: (row) => <CellStack title={row.contractId} meta={row.validationLane} />,
            },
            { key: "tool", header: "Tool" },
            {
              key: "currentEvidenceState",
              header: "Evidence state",
              render: (row) => (
                <StatusPill tone={row.currentEvidenceState === "promoted-currently-reproducible" ? "green" : "amber"}>
                  {row.currentEvidenceState}
                </StatusPill>
              ),
            },
            { key: "closureState", header: "Closure" },
            {
              key: "independentComparison",
              header: "Independent comparison",
              render: (row) => row.requiredIndependentComparison.status,
            },
            {
              key: "scenarioRefs",
              header: "Scenario refs",
              render: (row) => row.scenarioRefs.map((ref) => ref.scenarioId).join(", "),
            },
            { key: "nextAction", header: "Next action" },
            {
              key: "rowHash",
              header: "Hash",
              render: (row) => <code>{row.rowHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Package manifest" status={generatedResearchPackageManifest.mode}>
        <p>{generatedResearchPackageManifest.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Generated" value={generatedResearchPackageManifest.generatedAt.slice(0, 10)} />
          <Metric label="Commercial use" value={generatedResearchPackageManifest.commercialUseAllowed ? "clear" : "blocked"} />
          <Metric label="Customer dossiers" value={generatedCustomerDecisionDossierLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Report packets" value={generatedCustomerReportPacketLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Diligence orders" value={generatedScenarioDiligenceWorkOrderLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Decision deltas" value={generatedScenarioDecisionDeltaLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Capability ledger" value={generatedCapabilityMaturityLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Validation ledger" value={generatedModelValidationProtocolLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Economics evidence" value={generatedEconomicsEvidenceLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Process evidence" value={generatedMaterialsProcessEvidenceLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Environment evidence" value={generatedSpaceEnvironmentEvidenceLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Workspace ledger" value={generatedWorkspaceLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Ingestion ledger" value={generatedSourceIngestionLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Clearance dossier" value={generatedSourceUseClearanceDossier.dossierHash.slice(0, 18)} />
          <Metric label="Release ledger" value={generatedReleaseEvidenceLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Closure ledger" value={generatedPromotionClosureLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Decision ledger" value={generatedScenarioDecisionLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Audit ledger" value={generatedScenarioDecisionAuditLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Crossover ledger" value={generatedScenarioCrossoverFrontierLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Run ledger" value={generatedModelRunLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Report" value={generatedFeasibilityReport.reportHash} />
        </div>
      </EvidencePanel>
      <EvidencePanel title="Business decision memo" status={generatedFeasibilityReport.decisionMemo.customerUseState} tone="red">
        <p>{generatedFeasibilityReport.decisionMemo.investmentQuestion}</p>
        <p>{generatedFeasibilityReport.decisionMemo.currentAnswer}</p>
        <div className="metric-grid-4">
          <Metric label="Selected scenario" value={generatedFeasibilityReport.decisionMemo.selectedScenarioName} />
          <Metric label="Top decision" value={generatedFeasibilityReport.decisionMemo.topDecisionScenarioId} />
          <Metric label="Decision lane" value={generatedFeasibilityReport.decisionMemo.selectedDecisionLane?.replace("lane-", "L") ?? "n/a"} />
          <Metric label="Confidence" value={generatedFeasibilityReport.decisionMemo.confidenceBand} />
          <Metric label="Sellable lane" value={generatedFeasibilityReport.decisionMemo.sellableBetaLane} />
          <Metric label="Commercialization" value={generatedFeasibilityReport.decisionMemo.commercializationAllowed ? "allowed" : "blocked"} />
          <Metric label="Memo hash" value={generatedFeasibilityReport.decisionMemo.memoHash} />
          <Metric label="Source-use hash" value={generatedFeasibilityReport.decisionMemo.evidenceHashes.sourceUseRightsLedger?.slice(0, 18) ?? "n/a"} />
        </div>
        <DataTable
          shell="shell"
          rows={generatedFeasibilityReport.decisionMemo.nextProofActions}
          getRowKey={(action) => action.id}
          columns={[
            { key: "title", header: "Next proof action" },
            { key: "lane", header: "Lane" },
            { key: "state", header: "State" },
            { key: "nextAction", header: "Action" },
          ]}
        />
        <StackList
          items={generatedFeasibilityReport.decisionMemo.topBlockers.slice(0, 4).map((blocker) => ({
            key: blocker,
            title: "Blocker",
            body: blocker,
          }))}
          getTitle={(item) => item.title}
          getBody={(item) => item.body}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Customer report packets"
        status={generatedCustomerReportPacketLedger.mode}
        className="panel-large"
      >
        <p>{generatedCustomerReportPacketLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Report packets" value={generatedCustomerReportPacketLedger.reportPacketCount} />
          <Metric label="Blocked packets" value={generatedCustomerReportPacketLedger.blockedReportPacketCount} />
          <Metric label="Markdown exports" value={generatedCustomerReportPacketLedger.reportExportCount} />
          <Metric label="Customer ready" value={generatedCustomerReportPacketLedger.customerReadyReportPacketCount} />
          <Metric label="Ledger hash" value={generatedCustomerReportPacketLedger.ledgerHash.slice(0, 18)} />
        </div>
        <DataTable
          rows={generatedCustomerReportPacketLedger.reportPackets}
          getRowKey={(packet) => packet.id}
          columns={[
            {
              key: "title",
              header: "Packet",
              render: (packet) => <CellStack title={packet.title} meta={packet.customerQuestion} />,
            },
            {
              key: "reportState",
              header: "State",
              render: (packet) => <StatusPill tone="amber">{packet.reportState}</StatusPill>,
            },
            {
              key: "executiveAnswer",
              header: "Executive answer",
              render: (packet) => packet.executiveAnswer.currentAnswer,
            },
            {
              key: "blockedProofGates",
              header: "Proof gates",
              render: (packet) => packet.blockedProofGates.map((gate) => gate.id).join(", "),
            },
            {
              key: "blockedExportBoundary",
              header: "Export boundary",
              render: (packet) => packet.exportContract.blockedExportBoundary,
            },
            {
              key: "reportPacketHash",
              header: "Hash",
              render: (packet) => <code>{packet.reportPacketHash.slice(0, 18)}</code>,
            },
          ]}
        />
        <h3>Markdown review exports</h3>
        <DataTable
          rows={generatedCustomerReportPacketLedger.reportExportManifest.entries}
          getRowKey={(entry) => entry.packetId}
          columns={[
            {
              key: "title",
              header: "Export",
              render: (entry) => <CellStack title={entry.title} meta={entry.format} />,
            },
            {
              key: "reportState",
              header: "State",
              render: (entry) => <StatusPill tone="amber">{entry.reportState}</StatusPill>,
            },
            {
              key: "path",
              header: "Path",
              render: (entry) => <code>{entry.path}</code>,
            },
            { key: "blockedExportBoundary", header: "Boundary" },
            {
              key: "contentHash",
              header: "Content hash",
              render: (entry) => <code>{entry.contentHash.slice(0, 18)}</code>,
            },
            {
              key: "exportHash",
              header: "Export hash",
              render: (entry) => <code>{entry.exportHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Customer decision dossiers"
        status={generatedCustomerDecisionDossierLedger.mode}
        className="panel-large"
      >
        <p>{generatedCustomerDecisionDossierLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Scenarios covered" value={generatedCustomerDecisionDossierLedger.scenarioCount} />
          <Metric label="Paid beta" value={generatedCustomerDecisionDossierLedger.paidBetaDossierCount} />
          <Metric label="Customer ready" value={generatedCustomerDecisionDossierLedger.customerReviewReadyCount} />
          <Metric label="Ledger hash" value={generatedCustomerDecisionDossierLedger.ledgerHash.slice(0, 18)} />
        </div>
        <DataTable
          rows={generatedCustomerDecisionDossierLedger.dossiers}
          getRowKey={(dossier) => dossier.id}
          columns={[
            {
              key: "name",
              header: "Scenario",
              render: (dossier) => <CellStack title={dossier.name} meta={dossier.summary} />,
            },
            {
              key: "customerUseState",
              header: "Use state",
              render: (dossier) => (
                <StatusPill tone={dossier.customerUseState === "customer-review-ready" ? "green" : "amber"}>
                  {dossier.customerUseState}
                </StatusPill>
              ),
            },
            {
              key: "decisionReadinessScore",
              header: "Readiness",
              render: (dossier) => dossier.metrics.decisionReadinessScore,
            },
            {
              key: "uncertainty",
              header: "Uncertainty",
              render: (dossier) =>
                `${dossier.metrics.uncertaintyBudgetPoints} / ${dossier.metrics.uncertaintyClass}`,
            },
            {
              key: "proofToChangeAnswer",
              header: "Proof to change answer",
              render: (dossier) =>
                dossier.proofToChangeAnswer
                  ? `${dossier.proofToChangeAnswer.requiredUtilityDelta} utility`
                  : "n/a",
            },
            {
              key: "blockingProof",
              header: "Blocking proof",
              render: (dossier) => dossier.nextProofActions[0],
            },
            {
              key: "dossierHash",
              header: "Hash",
              render: (dossier) => <code>{dossier.dossierHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Diligence work orders"
        status={generatedScenarioDiligenceWorkOrderLedger.mode}
        className="panel-large"
      >
        <p>{generatedScenarioDiligenceWorkOrderLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Work orders" value={generatedScenarioDiligenceWorkOrderLedger.workOrderCount} />
          <Metric label="Blocked" value={generatedScenarioDiligenceWorkOrderLedger.blockedWorkOrderCount} />
          <Metric label="P0 coverage" value={`${generatedScenarioDiligenceWorkOrderLedger.coveredP0GapCount}/${generatedScenarioDiligenceWorkOrderLedger.p0GapCount}`} />
          <Metric label="Customer use" value={generatedScenarioDiligenceWorkOrderLedger.customerUseAllowed ? "clear" : "blocked"} />
        </div>
        <DataTable
          rows={generatedScenarioDiligenceWorkOrderLedger.workOrders.slice(0, 8)}
          getRowKey={(order) => order.id}
          columns={[
            {
              key: "title",
              header: "Work order",
              render: (order) => <CellStack title={order.title} meta={order.nextAction} />,
            },
            {
              key: "lane",
              header: "Lane",
              render: (order) => (
                <StatusPill tone={order.priorityLane === "P0-critical-path" ? "red" : "amber"}>
                  {order.lane}
                </StatusPill>
              ),
            },
            { key: "buyerQuestion", header: "Customer question" },
            {
              key: "affectedPackageIds",
              header: "Packages",
              render: (order) => order.affectedPackageIds.join(", "),
            },
            {
              key: "decisionDelta",
              header: "Decision delta",
              render: (order) => order.decisionDeltaContract.materialChangeRule,
            },
            {
              key: "replay",
              header: "Replay",
              render: (order) => <code>{order.replayContract.planningCommand}</code>,
            },
            {
              key: "workOrderHash",
              header: "Hash",
              render: (order) => <code>{order.workOrderHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Decision delta acceptance"
        status={generatedScenarioDecisionDeltaLedger.mode}
        className="panel-large"
      >
        <p>{generatedScenarioDecisionDeltaLedger.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Delta records" value={generatedScenarioDecisionDeltaLedger.deltaRecordCount} />
          <Metric label="Blocked deltas" value={generatedScenarioDecisionDeltaLedger.blockedDeltaRecordCount} />
          <Metric label="Accepted deltas" value={generatedScenarioDecisionDeltaLedger.acceptedDeltaRecordCount} />
          <Metric label="Material changes" value={generatedScenarioDecisionDeltaLedger.materialChangeDetectedCount} />
        </div>
        <DataTable
          rows={generatedScenarioDecisionDeltaLedger.deltaRecords.slice(0, 8)}
          getRowKey={(record) => record.id}
          columns={[
            {
              key: "buyerQuestion",
              header: "Delta record",
              render: (record) => <CellStack title={record.buyerQuestion} meta={record.workOrderId} />,
            },
            {
              key: "acceptanceState",
              header: "State",
              render: (record) => <StatusPill tone="amber">{record.acceptanceState}</StatusPill>,
            },
            {
              key: "baseline",
              header: "Baseline scenarios",
              render: (record) => record.baseline.scenarios.map((scenario) => scenario.scenarioId).join(", "),
            },
            {
              key: "requiredFields",
              header: "Required after-run fields",
              render: (record) => record.afterRunArtifactRequired.requiredFields.join(", "),
            },
            {
              key: "blockedReason",
              header: "Blocked reason",
              render: (record) => record.deltaSummary.blockedReason,
            },
            {
              key: "deltaHash",
              header: "Hash",
              render: (record) => <code>{record.deltaHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Sidecar readiness review"
        status={generatedSidecarReadinessLedger.mode}
        tone={generatedSidecarReadinessLedger.platformRuntimeReady ? "green" : "amber"}
      >
        <div className="metric-grid-4">
          <Metric label="Readiness contracts" value={generatedSidecarReadinessLedger.contractCount} />
          <Metric label="Runtime promoted" value={generatedSidecarReadinessLedger.promotedRuntimeContractCount} />
          <Metric label="Current reproducible" value={generatedSidecarReadinessLedger.currentReproduciblePromotedContractCount} />
          <Metric label="Repro debt" value={generatedSidecarReadinessLedger.promotedButNotCurrentlyReproducibleCount} />
          <Metric label="Ready to run" value={generatedSidecarReadinessLedger.readyToRunContractCount} />
          <Metric label="Runtime blocked" value={generatedSidecarReadinessLedger.blockedRuntimeContractCount} />
          <Metric label="Env drift" value={generatedSidecarReadinessLedger.environmentDriftContractCount} />
          <Metric label="Readiness hash" value={generatedSidecarReadinessLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Preflight reports" value={generatedSidecarReadinessLedger.preflightReportCount} />
          <Metric label="Promoted artifacts" value={generatedSidecarPromotionLedger.promotedCount} />
          <Metric label="Promoted contracts" value={generatedSidecarPromotionLedger.promotedContractCount} />
        </div>
        <p>{generatedSidecarReadinessLedger.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedSidecarPromotionLedger.validations.filter((validation) => validation.promotionAllowed)}
          getRowKey={(validation) => `package-${validation.reportPath}-${validation.artifactId}`}
          columns={[
            { key: "artifactId", header: "Promoted artifact" },
            { key: "contractId", header: "Contract" },
            { key: "scenarioId", header: "Scenario" },
            {
              key: "fieldCoveragePercent",
              header: "Coverage",
              render: (validation) => `${validation.fieldCoveragePercent}%`,
            },
            {
              key: "artifactHash",
              header: "Artifact hash",
              render: (validation) => validation.artifactHash?.slice(0, 24),
            },
          ]}
        />
        <DataTable
          shell="shell"
          rows={runtimeDependencyRows}
          getRowKey={(record) => record.contractId}
          columns={[
            {
              key: "tool",
              header: "Dependency lane",
              render: (record) => <CellStack title={record.tool} meta={record.contractId} />,
            },
            {
              key: "acquisitionState",
              header: "Custody state",
              render: (record) => (
                <StatusPill tone={record.acquisitionState === "ready-for-runtime-artifact-generation" ? "green" : "amber"}>
                  {record.acquisitionState}
                </StatusPill>
              ),
            },
            { key: "missingDependencyCount", header: "Missing deps" },
            {
              key: "blocked",
              header: "Blocked probes",
              render: (record) =>
                record.dependencyItems
                  .filter((item) => item.dependencyState === "blocked-missing-or-unreviewed")
                  .map((item) => item.probeId)
                  .join(", ") || "none",
            },
            { key: "nextAction", header: "Next action" },
          ]}
        />
        <DataTable
          shell="shell"
          rows={generatedSidecarReadinessLedger.contracts}
          getRowKey={(contract) => contract.contractId}
          columns={[
            { key: "tool", header: "Contract" },
            { key: "readinessState", header: "State" },
            { key: "reproducibilityState", header: "Reproducibility" },
            {
              key: "latestPreflight",
              header: "Latest preflight",
              render: (contract) => contract.latestPreflight?.preflightState ?? "no preflight",
            },
            {
              key: "bestActionablePreflight",
              header: "Best preflight",
              render: (contract) => contract.bestActionablePreflight?.preflightState ?? "no preflight",
            },
            {
              key: "blockedProbeIds",
              header: "Blocked probes",
              render: (contract) => contract.latestPreflight?.blockedProbeIds?.join(", ") || "none",
            },
            { key: "nextAction", header: "Next action" },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Promotion closure sequence"
        status={generatedPromotionClosureLedger.mode}
        tone={generatedPromotionClosureLedger.promotionAllowed ? "green" : "amber"}
        className="panel-large"
      >
        <p>{generatedPromotionClosureLedger.operatingRule}</p>
        <DataTable
          rows={generatedPromotionClosureLedger.closureSequence}
          getRowKey={(item) => item.releaseGateId}
          columns={[
            { key: "releaseGateId", header: "Gate" },
            {
              key: "closureState",
              header: "State",
              render: (item) => (
                <StatusPill
                  tone={item.closureState === "closed" ? "green" : item.closureState === "partial-proof" ? "blue" : "amber"}
                >
                  {item.closureState}
                </StatusPill>
              ),
            },
            { key: "priority", header: "Priority" },
            { key: "firstAction", header: "First action" },
            {
              key: "closurePlanHash",
              header: "Closure hash",
              render: (item) => <code>{item.closurePlanHash.slice(0, 20)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Crossover frontier" status="decision support" className="panel-large">
        <p>{generatedScenarioCrossoverFrontierLedger.operatingRule}</p>
        <DataTable
          rows={generatedScenarioCrossoverFrontierLedger.crossovers.slice(0, 5)}
          getRowKey={(crossover) => crossover.id}
          columns={[
            {
              key: "challengerName",
              header: "Challenger",
              render: (crossover) => <CellStack title={crossover.challengerName} meta={`vs ${crossover.currentLeaderName}`} />,
            },
            { key: "requiredUtilityDelta", header: "Gap" },
            {
              key: "crossoverState",
              header: "State",
              render: (crossover) => <StatusPill tone="amber">{crossover.crossoverState}</StatusPill>,
            },
            {
              key: "topLever",
              header: "Top lever",
              render: (crossover) => {
                const lever = crossover.topSingleLeverPaths[0];
                return lever ? `${lever.criterion} ${lever.direction}` : "n/a";
              },
            },
            {
              key: "requiredRawDelta",
              header: "Required delta",
              render: (crossover) => crossover.topSingleLeverPaths[0]?.requiredRawDelta ?? "n/a",
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Materials/process proof gates"
        status={generatedMaterialsProcessEvidenceLedger.promotionAllowed ? "promoted" : "blocked"}
        tone="red"
        className="panel-large"
      >
        <p>{generatedMaterialsProcessEvidenceLedger.operatingRule}</p>
        <DataTable
          rows={generatedMaterialsProcessEvidenceLedger.records}
          getRowKey={(record) => record.processId}
          columns={[
            {
              key: "processName",
              header: "Process",
              render: (record) => <CellStack title={record.processName} meta={record.evidenceFamily} />,
            },
            { key: "evidenceScore", header: "Evidence score" },
            {
              key: "bestScenarioFit",
              header: "Best scenario fit",
              render: (record) => record.bestScenarioFit?.scenarioName ?? "n/a",
            },
            {
              key: "requiredRuntimeArtifacts",
              header: "Required runtime artifact",
              render: (record) => record.requiredRuntimeArtifacts[0],
            },
            {
              key: "promotionGate",
              header: "State",
              render: (record) => <StatusPill tone="amber">{record.promotionGate.state}</StatusPill>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Space environment proof gates"
        status={generatedSpaceEnvironmentEvidenceLedger.promotionAllowed ? "promoted" : "blocked"}
        tone="red"
        className="panel-large"
      >
        <p>{generatedSpaceEnvironmentEvidenceLedger.operatingRule}</p>
        <DataTable
          rows={generatedSpaceEnvironmentEvidenceLedger.records}
          getRowKey={(record) => record.id}
          columns={[
            {
              key: "label",
              header: "Evidence lane",
              render: (record) => <CellStack title={record.label} meta={record.evidenceFamily} />,
            },
            { key: "evidenceScore", header: "Score" },
            {
              key: "highestStressScenario",
              header: "Highest stress scenario",
              render: (record) => record.highestStressScenario?.scenarioName ?? "n/a",
            },
            {
              key: "requiredPayloads",
              header: "Required payload",
              render: (record) => record.requiredPayloads[0],
            },
            {
              key: "promotionGate",
              header: "State",
              render: (record) => <StatusPill tone="amber">{record.promotionGate.state}</StatusPill>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Package lanes" status="beta blocked" className="panel-large">
        <DataTable
          rows={packageRows}
          getRowKey={(item) => item.id}
          columns={[
            { key: "title", header: "Package" },
            {
              key: "status",
              header: "Status",
              render: (item) => (
                <StatusPill tone={item.status === "approval-ready" ? "green" : "amber"}>
                  {item.status}
                </StatusPill>
              ),
            },
            { key: "customerContractState", header: "Contract" },
            { key: "customerQuestion", header: "Customer question" },
            {
              key: "workspace",
              header: "Workspace",
              render: (item) => item.workspaceRef.title,
            },
            {
              key: "runs",
              header: "Runs",
              render: (item) => item.runRefs.map((run) => run.selectedScenarioId).join(", "),
            },
            { key: "artifactCount", header: "Artifacts" },
            {
              key: "blockedProofGateCount",
              header: "Proof gates",
              render: (item) => `${item.blockedProofGateCount} blocked`,
            },
            {
              key: "packageHash",
              header: "Hash",
              render: (item) => <code>{item.packageHash.slice(0, 20)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <div className="module-grid">
        {packages.map((item) => (
          <EvidencePanel title={item.title} status={`${item.blockers.length} blockers`} key={item.id}>
            <StackList
              items={[
                {
                  key: "buyer-fit",
                  title: "Buyer fit",
                  meta: item.buyerFit.join(", "),
                  body: item.deliverableType,
                },
                {
                  key: "review-required",
                  title: "Review required",
                  meta: item.reviewRequired.join(", "),
                  body: item.allowedUse,
                },
                {
                  key: "customer-contract",
                  title: "Customer contract",
                  meta: `${item.customerReadinessContract.state}; commercial use ${
                    item.customerReadinessContract.commercialUseAllowed ? "clear" : "blocked"
                  }`,
                  body: item.customerReadinessContract.customerUseBoundary,
                },
                {
                  key: "proof-gates",
                  title: "Proof gates",
                  meta: item.customerReadinessContract.blockedProofGateIds.join(", "),
                  body: (
                    <>
                      Current runtime preflight{" "}
                      {item.customerReadinessContract.currentRuntimePreflight.reportHash?.slice(0, 18) ?? "missing"};
                      ready {item.customerReadinessContract.currentRuntimePreflight.readyContractIds.length}, blocked{" "}
                      {item.customerReadinessContract.currentRuntimePreflight.blockedContractIds.length}
                    </>
                  ),
                },
                {
                  key: "blocked-customer-claims",
                  title: "Blocked customer claims",
                  meta: item.customerReadinessContract.blockedClaims.slice(0, 2).join(" | "),
                  body: `Contract hash ${item.customerReadinessContract.contractHash.slice(0, 18)}`,
                },
                {
                  key: "blocking-gates",
                  title: "Blocking gates",
                  meta: item.blockers.join(" | "),
                  body: `Workspace hash ${item.workspaceRef.workspaceHash.slice(0, 18)}; package hash ${item.packageHash.slice(0, 18)}`,
                },
              ]}
              getTitle={(record) => record.title}
              getMeta={(record) => record.meta}
              getBody={(record) => record.body}
            />
          </EvidencePanel>
        ))}
      </div>
    </section>
  );
}
